import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_URLS } from '../config/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Code,
  Palette,
  Megaphone,
  BarChart3,
  Globe,
  Smartphone,
  Camera,
  FileText,
  Music,
  Video,
  Briefcase,
  TrendingUp,
  Users,
  Zap,
  ArrowRight,
  Star,
  Clock,
  CheckCircle,
  Server,
  Database,
  Layers,
  Target,
  Rocket,
  Heart,
  Award,
  Shield
} from 'lucide-react';

const CategoriesSection = ({ sectionContent }) => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Icon mapping for dynamic icons
  const iconMap = {
    Briefcase, Globe, Palette, Smartphone, Zap, Server, 
    Megaphone, Code, Database, Layers, Target, Rocket,
    Heart, Star, Award, Shield, Users, TrendingUp, CheckCircle, Clock
  };

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_URLS.PROJECT_CATEGORIES);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (Array.isArray(data)) {
          setCategories(data);
        } else if (data.status === 'success' && Array.isArray(data.data)) {
          setCategories(data.data);
        } else {
          setCategories([]);
        }
      } catch (error) {
        console.error('Error fetching project categories:', error);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-[#f5f3ff] via-white to-[#f5f3ff] relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A5AFF] mx-auto"></div>
            <p className="mt-4 text-gray-600">Projeler yükleniyor...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-[#f5f3ff] via-white to-[#f5f3ff] relative overflow-hidden">
      {/* Background Animations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-32 h-32 bg-[#7A5AFF]/20 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-48 h-48 bg-[#7A5AFF]/15 rounded-full opacity-15 animate-bounce"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-[#7A5AFF]/25 rounded-full opacity-25 animate-ping"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Layout: Left Cards, Right Text */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - 4 Category Cards (2x2 Grid) */}
          <div className="grid grid-cols-2 gap-6 order-2 lg:order-1">
            {categories.slice(0, 4).map((category, index) => (
              <Link
                key={category.id}
                to={`/kategori/${category.slug || category.id}`}
                className="block"
              >
                <Card
                  className={`group relative overflow-hidden bg-white border-0 transform hover:scale-105 transition-all duration-500 hover:shadow-2xl cursor-pointer h-full`}
                  onMouseEnter={() => setHoveredCard(category.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  style={{
                    animationDelay: `${index * 0.1}s`,
                    animation: 'fadeInUp 0.6s ease-out forwards'
                  }}
                >
                  {/* Animated Background Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>

                  {/* Trend Indicator */}
                  {category.trend && (
                    <div className="absolute top-3 right-3 z-10">
                      <div className="flex items-center space-x-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                        <TrendingUp className="w-3 h-3" />
                        <span>{category.trend}</span>
                      </div>
                    </div>
                  )}

                  <CardContent className="p-6 space-y-4 h-full flex flex-col">
                    {/* Category Icon */}
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                      {(() => {
                        const IconComponent = iconMap[category.icon] || Briefcase;
                        return <IconComponent className="w-7 h-7 text-white" />;
                      })()}
                    </div>

                    {/* Category Info */}
                    <div className="space-y-2 flex-grow">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#7A5AFF] transition-colors duration-300">
                        {category.name}
                      </h3>
                      <div className="text-sm text-gray-600 font-medium flex items-center space-x-2">
                        <span>{category.count}</span>
                        <div className="w-1.5 h-1.5 bg-[#7A5AFF] rounded-full"></div>
                      </div>
                    </div>

                    {/* View Button */}
                    <div className="flex items-center justify-between text-[#7A5AFF] group-hover:text-[#5a3fd9] font-medium text-sm">
                      <span>Detaylı İncele</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>

                  {/* Hover Effect Particles */}
                  {hoveredCard === category.id && (
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-[#7A5AFF] rounded-full animate-ping"></div>
                      <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-[#7A5AFF]/80 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
                      <div className="absolute top-1/2 right-1/3 w-1 h-1 bg-[#9d7dff] rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
                    </div>
                  )}
                </Card>
              </Link>
            ))}
          </div>

          {/* Right Side - Text Content */}
          <div className="space-y-6 text-center md:text-left order-1 lg:order-2">
            <div className="inline-flex items-center space-x-2 bg-[#7A5AFF]/15 text-[#7A5AFF] px-6 py-3 rounded-full text-sm font-medium transform hover:scale-105 transition-transform duration-300">
              <Briefcase className="w-5 h-5 animate-bounce" />
              <span>{sectionContent?.badge_text || 'Portföy'}</span>
              <Badge variant="secondary" className="ml-2 animate-pulse">Yeni</Badge>
            </div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              <span className="inline-block transform hover:rotate-2 transition-transform duration-300">
                {sectionContent?.title || 'Projelerimiz'}
              </span>
            </h2>

            <p className="text-xl text-gray-600 leading-relaxed">
              {sectionContent?.description || 'Gerçekleştirdiğimiz başarılı projeleri keşfedin. Farklı sektörlerden örnek çalışmalarımızı inceleyin.'}
            </p>

            {/* Stats - 2x2 Grid */}
            <div className="grid grid-cols-2 gap-4 pt-4 max-w-md mx-auto md:max-w-none md:mx-0">
              {(sectionContent?.stats || [
                { icon: 'Users', label: '150+ Başarılı Proje', color: 'text-blue-600' },
                { icon: 'CheckCircle', label: '100% Müşteri Memnuniyeti', color: 'text-green-600' },
                { icon: 'Star', label: '5/5 Proje Kalitesi', color: 'text-yellow-600' },
                { icon: 'Clock', label: '7/24 Teknik Destek', color: 'text-[#7A5AFF]' }
              ]).map((stat, index) => {
                const StatIcon = iconMap[stat.icon] || Users;
                return (
                  <div key={index} className="flex items-center space-x-3 transform hover:scale-105 transition-transform duration-300 justify-center md:justify-start">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${stat.color === 'text-blue-600' ? 'from-[#7A5AFF]/15 to-[#7A5AFF]/25' : stat.color === 'text-green-600' ? 'from-green-100 to-green-200' : stat.color === 'text-yellow-600' ? 'from-yellow-100 to-yellow-200' : 'from-[#7A5AFF]/15 to-[#7A5AFF]/25'} flex items-center justify-center`}>
                      <StatIcon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                    <span className="text-gray-700 font-medium text-sm">{stat.label}</span>
                  </div>
                );
              })}
            </div>

            {/* Tüm Projeler Button */}
            <div className="pt-6 flex justify-center md:justify-start">
              <Link to={sectionContent?.button_link || '/projeler'}>
                <Button size="lg" className="bg-gradient-to-r from-[#7A5AFF] to-[#5a3fd9] hover:from-[#8b6bff] hover:to-[#6b4de6] text-white transform hover:scale-105 transition-all duration-300 text-lg px-8 py-6 shadow-lg shadow-[#7A5AFF]/30">
                  <Briefcase className="w-5 h-5 mr-2" />
                  {sectionContent?.button_text || 'Tüm Projeleri Görüntüle'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CategoriesSection;